import { Building2, Coins } from 'lucide-react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ImageWithFallback } from '@/components/figma/ImageWithFallback';
import { RestaurantCard } from '@/components/RestaurantCard';
import { getRestaurantPath } from '@/config/routes';
import { useLanguage } from '@/contexts/AppContext';
import { getStoreOpenLabel } from '@/lib/storeOpenLabel';
import type { Locale } from '@/config/locales';
import type { Store } from '@/lib/api/types';
import type { PointsOrganizationSection } from '@/hooks/usePointsPortfolio';

interface OrganizationPointsOverviewProps {
  organizations: PointsOrganizationSection[];
  highlightedBrandId?: string | null;
  highlightedStoreId?: string | null;
}

const dayKeys = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
type DayKey = (typeof dayKeys)[number];

function formatNumber(value: number, locale: Locale): string {
  return value.toLocaleString(locale);
}

const labels = {
  ko: {
    empty: '표시할 브랜드와 매장이 없습니다.',
    standaloneStores: '매장',
    storeCount: (count: number) => `${formatNumber(count, 'ko')}개 지점`,
    missingAddress: '주소 미등록',
    brandStoresAria: (name: string) => `${name} 매장`,
    standaloneStoresAria: '브랜드 없는 매장',
  },
  es: {
    empty: 'No hay marcas ni tiendas para mostrar.',
    standaloneStores: 'Tiendas',
    storeCount: (count: number) => `${formatNumber(count, 'es')} ${count === 1 ? 'sucursal' : 'sucursales'}`,
    missingAddress: 'Dirección no registrada',
    brandStoresAria: (name: string) => `Sucursales de ${name}`,
    standaloneStoresAria: 'Sucursales sin marca',
  },
  en: {
    empty: 'No brands or stores to show.',
    standaloneStores: 'Stores',
    storeCount: (count: number) => `${formatNumber(count, 'en')} ${count === 1 ? 'store' : 'stores'}`,
    missingAddress: 'Address not set',
    brandStoresAria: (name: string) => `${name} stores`,
    standaloneStoresAria: 'Stores without brand',
  },
  pt: {
    empty: 'Não há marcas nem lojas para exibir.',
    standaloneStores: 'Lojas',
    storeCount: (count: number) => `${formatNumber(count, 'pt')} ${count === 1 ? 'loja' : 'lojas'}`,
    missingAddress: 'Endereço não registrado',
    brandStoresAria: (name: string) => `Lojas de ${name}`,
    standaloneStoresAria: 'Lojas sem marca',
  },
  fr: {
    empty: 'Aucune marque ni aucun magasin à afficher.',
    standaloneStores: 'Magasins',
    storeCount: (count: number) => `${formatNumber(count, 'fr')} ${count === 1 ? 'magasin' : 'magasins'}`,
    missingAddress: 'Adresse non enregistrée',
    brandStoresAria: (name: string) => `Magasins de ${name}`,
    standaloneStoresAria: 'Magasins sans marque',
  },
  zh: {
    empty: '没有可显示的品牌或门店。',
    standaloneStores: '门店',
    storeCount: (count: number) => `${formatNumber(count, 'zh')} 家门店`,
    missingAddress: '地址未登记',
    brandStoresAria: (name: string) => `${name} 门店`,
    standaloneStoresAria: '无品牌门店',
  },
  ja: {
    empty: '表示するブランドや店舗はありません。',
    standaloneStores: '店舗',
    storeCount: (count: number) => `${formatNumber(count, 'ja')}店舗`,
    missingAddress: '住所未登録',
    brandStoresAria: (name: string) => `${name}の店舗`,
    standaloneStoresAria: 'ブランドなし店舗',
  },
  hi: {
    empty: 'दिखाने के लिए कोई ब्रांड या स्टोर नहीं हैं।',
    standaloneStores: 'स्टोर',
    storeCount: (count: number) => `${formatNumber(count, 'hi')} स्टोर`,
    missingAddress: 'पता दर्ज नहीं है',
    brandStoresAria: (name: string) => `${name} स्टोर`,
    standaloneStoresAria: 'बिना ब्रांड वाले स्टोर',
  },
  de: {
    empty: 'Keine Marken oder Filialen zum Anzeigen.',
    standaloneStores: 'Filialen',
    storeCount: (count: number) => `${formatNumber(count, 'de')} ${count === 1 ? 'Filiale' : 'Filialen'}`,
    missingAddress: 'Adresse nicht hinterlegt',
    brandStoresAria: (name: string) => `${name} Filialen`,
    standaloneStoresAria: 'Filialen ohne Marke',
  },
  it: {
    empty: 'Nessun marchio o negozio da mostrare.',
    standaloneStores: 'Negozi',
    storeCount: (count: number) => `${formatNumber(count, 'it')} ${count === 1 ? 'negozio' : 'negozi'}`,
    missingAddress: 'Indirizzo non registrato',
    brandStoresAria: (name: string) => `Negozi di ${name}`,
    standaloneStoresAria: 'Negozi senza marchio',
  },
  ru: {
    empty: 'Нет брендов или филиалов для отображения.',
    standaloneStores: 'Филиалы',
    storeCount: (count: number) => `${formatNumber(count, 'ru')} ${russianStoreNoun(count)}`,
    missingAddress: 'Адрес не указан',
    brandStoresAria: (name: string) => `Филиалы ${name}`,
    standaloneStoresAria: 'Филиалы без бренда',
  },
  ar: {
    empty: 'لا توجد علامات تجارية أو فروع لعرضها.',
    standaloneStores: 'الفروع',
    storeCount: (count: number) => `${formatNumber(count, 'ar')} فرع`,
    missingAddress: 'العنوان غير مسجل',
    brandStoresAria: (name: string) => `فروع ${name}`,
    standaloneStoresAria: 'فروع بلا علامة تجارية',
  },
  vi: {
    empty: 'Không có thương hiệu hoặc cửa hàng để hiển thị.',
    standaloneStores: 'Cửa hàng',
    storeCount: (count: number) => `${formatNumber(count, 'vi')} cửa hàng`,
    missingAddress: 'Chưa đăng ký địa chỉ',
    brandStoresAria: (name: string) => `Cửa hàng ${name}`,
    standaloneStoresAria: 'Cửa hàng không có thương hiệu',
  },
  th: {
    empty: 'ไม่มีแบรนด์หรือสาขาให้แสดง',
    standaloneStores: 'สาขา',
    storeCount: (count: number) => `${formatNumber(count, 'th')} สาขา`,
    missingAddress: 'ยังไม่ได้ลงทะเบียนที่อยู่',
    brandStoresAria: (name: string) => `สาขาของ ${name}`,
    standaloneStoresAria: 'สาขาที่ไม่มีแบรนด์',
  },
  nl: {
    empty: 'Geen merken of winkels om te tonen.',
    standaloneStores: 'Winkels',
    storeCount: (count: number) => `${formatNumber(count, 'nl')} ${count === 1 ? 'winkel' : 'winkels'}`,
    missingAddress: 'Adres niet ingesteld',
    brandStoresAria: (name: string) => `${name} winkels`,
    standaloneStoresAria: 'Winkels zonder merk',
  },
} satisfies Record<Locale, {
  empty: string;
  standaloneStores: string;
  storeCount: (count: number) => string;
  missingAddress: string;
  brandStoresAria: (name: string) => string;
  standaloneStoresAria: string;
}>;

const ALL_DAY_LABEL = {
  ko: '24시간',
  es: '24 h',
  en: '24 h',
  pt: '24 h',
  fr: '24 h',
  zh: '24小时',
  ja: '24時間',
  hi: '24 घंटे',
  de: '24 Std.',
  it: '24 h',
  ru: '24 ч',
  ar: '24 ساعة',
  vi: '24 giờ',
  th: '24 ชม.',
  nl: '24 uur',
} satisfies Record<Locale, string>;

/**
 * 요일별 정기 휴무 라벨 (#651 / admin#1849) — HoursModal `closed` 번역과 동일 표기.
 */
const CLOSED_LABEL = {
  ko: '휴무',
  es: 'Cerrado',
  en: 'Closed',
  pt: 'Fechado',
  fr: 'Fermé',
  zh: '休息',
  ja: '休業',
  hi: 'बंद',
  de: 'Geschlossen',
  it: 'Chiuso',
  ru: 'Закрыто',
  ar: 'مغلق',
  vi: 'Đóng cửa',
  th: 'ปิด',
  nl: 'Gesloten',
} satisfies Record<Locale, string>;

const HOURS_NOT_SET = {
  ko: '운영시간 미등록',
  es: 'Horario no registrado',
  en: 'Hours not set',
  pt: 'Horário não registrado',
  fr: 'Horaires non enregistrés',
  zh: '营业时间未登记',
  ja: '営業時間未登録',
  hi: 'समय दर्ज नहीं है',
  de: 'Öffnungszeiten nicht hinterlegt',
  it: 'Orario non registrato',
  ru: 'Часы не указаны',
  ar: 'ساعات العمل غير مسجلة',
  vi: 'Chưa đăng ký giờ hoạt động',
  th: 'ยังไม่ได้ลงทะเบียนเวลาเปิดทำการ',
  nl: 'Openingstijden niet ingesteld',
} satisfies Record<Locale, string>;

function russianStoreNoun(count: number): string {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 === 1 && mod100 !== 11) return 'филиал';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'филиала';
  return 'филиалов';
}

function getLabels(locale: Locale): (typeof labels)[Locale] {
  return labels[locale];
}

function getTodayKey(timezone?: string | null): DayKey {
  const now = new Date();

  if (timezone) {
    try {
      const weekday = new Intl.DateTimeFormat('en-US', {
        weekday: 'long',
        timeZone: timezone,
      }).format(now).toLowerCase();

      if ((dayKeys as readonly string[]).includes(weekday)) {
        return weekday as DayKey;
      }
    } catch (error) {
      if (!(error instanceof RangeError)) {
        throw error;
      }
    }
  }

  return dayKeys[now.getDay()];
}

function todayHoursLabel(store: Store, locale: Locale): string {
  if (store.is_24h) {
    return ALL_DAY_LABEL[locale];
  }

  const today = getTodayKey(store.timezone);
  const hours = store.hours?.[today];

  // #651 / admin#1849: 정기 휴무 요일은 open/close 시간이 보존되어 내려오므로
  // 시간 존재 여부보다 is_closed 를 먼저 판정. 부재(구버전 응답) 시 기존 분기 유지.
  // (is_24h=true 매장은 admin 측에서 is_closed=false 마스킹 — 위 is_24h 분기가 우선)
  if (hours?.is_closed) {
    return CLOSED_LABEL[locale];
  }

  if (hours?.open && hours?.close) {
    return `${hours.open}-${hours.close}`;
  }

  return HOURS_NOT_SET[locale];
}

function pointsText(points: number, locale: Locale): string {
  return `${points.toLocaleString(locale)} P`;
}

function storePlaceholderImage(storeId: number): string {
  const palettes = [
    ['#e8f3ef', '#1f7a5c'],
    ['#f6eadf', '#995f2b'],
    ['#e9edf7', '#435f9c'],
    ['#f4e8ee', '#944f72'],
  ] as const;
  const [background, foreground] = palettes[Math.abs(storeId) % palettes.length];
  const svg = [
    '<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300" viewBox="0 0 400 300">',
    `<rect width="400" height="300" rx="24" fill="${background}"/>`,
    `<rect x="116" y="92" width="168" height="116" rx="18" fill="${foreground}" opacity="0.14"/>`,
    `<path d="M136 204h128v-72H136v72Zm16-56h96v40h-96v-40Z" fill="${foreground}"/>`,
    `<path d="M124 132h152l-18-36H142l-18 36Z" fill="${foreground}" opacity="0.86"/>`,
    '</svg>',
  ].join('');

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

function StorePointCard({ store }: { store: Store }) {
  const navigate = useNavigate();
  const { locale } = useLanguage();
  const text = getLabels(locale);
  const q7Label = getStoreOpenLabel({
    isOpenNow: Boolean(store.is_open_now),
    status: store.status,
    closureReason: store.closure_reason,
    nextAvailableAt: store.next_available_at,
    storeTimezone: store.timezone,
  }, locale);

  return (
    <div id={`points-store-${store.id}`} className="scroll-mt-4">
      <RestaurantCard
        id={String(store.id)}
        name={store.name}
        cuisine={store.address || text.missingAddress}
        rating={store.rating || 0}
        deliveryTime={`${q7Label.text} · ${todayHoursLabel(store, locale)}`}
        distance=""
        imageUrl={store.image_url || storePlaceholderImage(store.id)}
        isOpen={Boolean(store.is_open_now)}
        q7Label={q7Label}
        showRating={false}
        onClick={(id) => navigate(getRestaurantPath(id))}
      />
    </div>
  );
}

export function OrganizationPointsOverview({
  organizations,
  highlightedBrandId,
  highlightedStoreId,
}: OrganizationPointsOverviewProps) {
  const { locale } = useLanguage();
  const text = getLabels(locale);

  useEffect(() => {
    const targetId = highlightedStoreId
      ? `points-store-${highlightedStoreId}`
      : highlightedBrandId
        ? `points-brand-${highlightedBrandId}`
        : null;

    if (!targetId) {
      return;
    }

    window.requestAnimationFrame(() => {
      document.getElementById(targetId)?.scrollIntoView({
        block: 'start',
        behavior: 'smooth',
      });
    });
  }, [highlightedBrandId, highlightedStoreId, organizations]);

  if (organizations.length === 0) {
    return (
      <div className="bg-background rounded-[8px] p-6 text-center text-sm text-muted-foreground">
        {text.empty}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {organizations.map((organization) => (
        <section key={organization.id} className="space-y-4">
          <header className="bg-background rounded-[8px] border border-border p-4">
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 rounded-[8px] overflow-hidden bg-primary/10 flex items-center justify-center shrink-0">
                {organization.logoUrl ? (
                  <ImageWithFallback
                    src={organization.logoUrl}
                    alt={organization.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Building2 className="w-6 h-6 text-primary" aria-hidden="true" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-[17px] font-semibold text-foreground truncate">{organization.name}</h2>
                <div className="mt-1 flex items-center gap-1 text-primary font-semibold text-[15px]">
                  <Coins className="w-4 h-4" aria-hidden="true" />
                  <span>{pointsText(organization.pointsBalance, locale)}</span>
                </div>
              </div>
            </div>
          </header>

          {organization.brands.map((brand) => (
            <section
              key={brand.id}
              id={`points-brand-${brand.id}`}
              className="scroll-mt-4 space-y-3"
              aria-label={text.brandStoresAria(brand.name)}
            >
              <div className="flex items-center gap-3 px-1">
                <div className="w-16 h-16 rounded-[8px] overflow-hidden bg-secondary/10 flex items-center justify-center shrink-0">
                  {brand.logoUrl ? (
                    <ImageWithFallback
                      src={brand.logoUrl}
                      alt={brand.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-secondary text-xl font-bold">{brand.name.charAt(0).toUpperCase()}</span>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="text-[16px] font-semibold text-foreground truncate">{brand.name}</h3>
                    <span className="text-[12px] px-2 py-1 rounded-[4px] bg-primary/10 text-primary font-semibold shrink-0">
                      {pointsText(brand.pointsBalance, locale)}
                    </span>
                  </div>
                  <p className="text-[12px] text-muted-foreground">{text.storeCount(brand.stores.length)}</p>
                </div>
              </div>

              <div>
                {brand.stores.map((store) => (
                  <StorePointCard key={store.id} store={store} />
                ))}
              </div>
            </section>
          ))}

          {organization.standaloneStores.length > 0 && (
            <section className="space-y-3" aria-label={text.standaloneStoresAria}>
              <h3 className="px-1 text-[15px] font-semibold text-foreground">{text.standaloneStores}</h3>
              <div>
                {organization.standaloneStores.map((store) => (
                  <StorePointCard key={store.id} store={store} />
                ))}
              </div>
            </section>
          )}
        </section>
      ))}
    </div>
  );
}
